import type { FC } from "react";
import { useCallback } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { type AddressStored } from "@jti/fe-common";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { selectAddresses } from "@jti/store/selectors/selectAddresses";
import { toggleSelectShippingAddress } from "@jti/store/slices/LEGACY__checkoutSlice";
import { AddressInfoBlock, SelectItemDialog, useApplyAddressOverrides, useTranslations } from "@jti/ui";

import styles from "./SelectShippingAddressDialog.module.scss";

type SelectShippingAddressDialogProps = {
  defaultSelectedAddressId?: string;
  provinceCode?: string;
  onSelectAddress(id: string): void;
  onCreateAddress(): void;
};

export const SelectShippingAddressDialog: FC<SelectShippingAddressDialogProps> = ({
  defaultSelectedAddressId,
  provinceCode,
  onSelectAddress,
  onCreateAddress,
}) => {
  const dispatch = useDispatch();
  const { __ } = useTranslations();
  const { COUNTRY_NAME } = useConfig();
  const { applyAddressOverrides } = useApplyAddressOverrides();

  const addresses = useSelector(selectAddresses);
  const isSelectingAddress = useSelector((state) => state.checkout.isSelectShippingAddress);
  const shippingSelectedAddressId = useSelector((state) => state.checkout.shippingSelectedAddressId);

  const closeModal = useCallback(() => {
    dispatch(toggleSelectShippingAddress(false));
  }, [dispatch]);

  const onSave = useCallback(
    (addressId: string) => {
      onSelectAddress(addressId);
      closeModal();
    },
    [dispatch, onSelectAddress]
  );

  const onCreateNew = useCallback(() => {
    onCreateAddress();
    closeModal();
  }, []);

  return (
    <SelectItemDialog<AddressStored>
      className={styles.addressDialog}
      isOpened={isSelectingAddress}
      title={__("cart.checkout.shipping_address.edit_dialog.title")}
      subTitle={__("cart.checkout.shipping_address.edit_dialog.subTitle")}
      createNew={{
        label: __("cart.checkout.shipping_address.edit_dialog.create_new"),
        onClick: onCreateNew,
      }}
      items={addresses}
      defaultSelectedId={defaultSelectedAddressId || shippingSelectedAddressId}
      onSave={onSave}
      onClose={closeModal}
      renderItem={(address) => (
        <AddressInfoBlock
          address={applyAddressOverrides(
            {
              ...address,
              countryName: COUNTRY_NAME,
            },
            { provinceCode }
          )}
          isEditable
        />
      )}
    />
  );
};
